import { useState } from 'react';
import type { z } from 'zod';
import { ApiError, NetworkError, apiPost } from '../api/client';
import type { ReviewPhase } from './SheetReview';

interface BatchRequest {
  readonly batchId: string;
  readonly rows: readonly unknown[];
  readonly confirmedEnds?: readonly string[] | undefined;
}

export interface ReviewState<Request, Response> {
  readonly batch: Request;
  readonly result: Response | null;
  readonly phase: ReviewPhase;
  /** Allocation ids whose ending the user ticked in the review. */
  readonly confirmed: ReadonlySet<string>;
}

export function failureText(error: unknown, fallback: string): string {
  if (error instanceof NetworkError) return `${fallback} אין חיבור לשרת.`;
  if (error instanceof ApiError && error.status === 409)
    return `${fallback} הנתונים השתנו במקביל על ידי משתמש אחר. לחצו שוב על "בדיקה ושמירה".`;
  if (error instanceof ApiError && (error.status === 403 || error.status === 404))
    return 'אין לך הרשאה לבצע פעולה זו.';
  if (error instanceof ApiError && error.requestId !== null)
    return `${fallback} מזהה בקשה: ${error.requestId}`;
  return fallback;
}

/**
 * The review → save flow shared by the Master Data sheets (DR-147): a READ ONLY preview, explicit
 * ticks for every end, then an atomic commit that carries the ticked ends. A lost answer can be
 * retried with the same batch id (it never writes twice); a rejected commit shows the server's
 * classification again, on a new batch id.
 */
export function useBatchReview<Request extends BatchRequest, Response>(options: {
  readonly previewPath: string;
  readonly commitPath: string;
  readonly schema: z.ZodType<Response>;
  /** Marks the draft rows with the server's classification. */
  readonly annotate: (result: Response) => void;
  readonly onSaved: (result: Response) => void;
  readonly onError: (text: string) => void;
}) {
  const [review, setReview] = useState<ReviewState<Request, Response> | null>(null);

  const start = async (batch: Request) => {
    const confirmed = new Set<string>();
    setReview({ batch, result: null, phase: 'reviewing', confirmed });
    try {
      const result = await apiPost(options.previewPath, batch, options.schema);
      options.annotate(result);
      setReview({ batch, result, phase: 'reviewing', confirmed });
    } catch (error) {
      setReview(null);
      options.onError(failureText(error, 'בדיקת השינויים נכשלה.'));
    }
  };

  const commit = async () => {
    if (review === null) return;
    // The ends the user ticked travel with the commit; the server re-checks every one of them.
    const batch: Request =
      review.phase === 'uncertain'
        ? review.batch
        : { ...review.batch, confirmedEnds: [...review.confirmed] };
    setReview({ ...review, batch, phase: 'saving' });
    try {
      const result = await apiPost(options.commitPath, batch, options.schema);
      setReview(null);
      options.onSaved(result);
    } catch (error) {
      if (error instanceof NetworkError) {
        // Outcome unknown: retrying with the same batch id cannot write twice.
        setReview({ ...review, batch, phase: 'uncertain' });
        return;
      }
      const rejected =
        error instanceof ApiError ? options.schema.safeParse(error.body['batch']) : null;
      if (rejected?.success === true) {
        options.annotate(rejected.data);
        setReview({
          ...review,
          batch: { ...batch, batchId: crypto.randomUUID() },
          result: rejected.data,
          phase: 'reviewing',
        });
      } else {
        setReview(null);
        options.onError(failureText(error, 'השמירה נכשלה. דבר לא נשמר.'));
      }
    }
  };

  const toggle = (allocationId: string, checked: boolean) => {
    setReview((current) => {
      if (current === null) return current;
      const confirmed = new Set(current.confirmed);
      if (checked) confirmed.add(allocationId);
      else confirmed.delete(allocationId);
      return { ...current, confirmed };
    });
  };

  return {
    review,
    start: (batch: Request) => void start(batch),
    commit: () => void commit(),
    toggle,
    close: () => {
      setReview(null);
    },
  };
}
